import { Editor } from '@tiptap/core';
import {
  getNodePosition,
  getStreamContentInsertPosition,
  scrollToLastNode,
} from 'helpers/stream/stream-node-helpers';
import type { HtmlFormattingFilterRow } from 'api/stream';

interface StreamToNodeParams {
  identifierType?: 'templateSectionId' | 'documentSectionId' | 'tempId';
  identifierValue?: string;
  shouldFilterByPromptId?: boolean;
}

interface StreamTipTapNodesParams {
  editor: Editor;
  identifierType?: 'templateSectionId' | 'documentSectionId';
  identifierValues?: string[];
  shouldFilterByPromptId?: boolean;
  token?: string;
  channelName: string;
  onStart?: () => void;
  onPostStream?: () => void;
  resolve?: boolean;
  /** When set, skips the real-time API and writes these token chunks per prompt id (templateSectionId). */
  mockHtmlTokensByPromptId?: Record<string, string[]>;
}

type TiptapJsonNode = {
  type?: string;
  attrs?: { templateSectionId?: string | number };
  content?: TiptapJsonNode[];
};

/** Document-order templateSectionId values for every RL_CUSTOM_SECTION in TipTap JSON. */
export const collectRlCustomSectionTemplateSectionIds = (
  docOrContent: TiptapJsonNode | TiptapJsonNode[] | null | undefined,
): string[] => {
  const ids: string[] = [];

  const visit = (node: TiptapJsonNode | null | undefined) => {
    if (!node || typeof node !== 'object') {
      return;
    }

    if (
      node.type === 'RL_CUSTOM_SECTION' &&
      node.attrs?.templateSectionId != null
    ) {
      ids.push(String(node.attrs.templateSectionId));
    }

    node.content?.forEach(visit);
  };

  if (Array.isArray(docOrContent)) {
    docOrContent.forEach(visit);
  } else if (docOrContent?.type === 'doc') {
    docOrContent.content?.forEach(visit);
  } else if (docOrContent) {
    visit(docOrContent);
  }

  return ids;
};

/**
 * Groups `HTML_FORMATTING` + `RUNNING` rows from the real-time filter API into
 * token chunk arrays keyed by `promptId`.
 */
export const groupHtmlFormattingFilterRowsByPromptId = (
  rows: HtmlFormattingFilterRow[],
): Record<string, string[]> => {
  const map: Record<string, string[]> = {};

  for (const row of rows) {
    if (
      row.step !== 'HTML_FORMATTING' ||
      row.status !== 'RUNNING' ||
      row.data?.token == null ||
      row.data?.promptId == null
    ) {
      continue;
    }

    const key = String(row.data.promptId);

    if (!map[key]) {
      map[key] = [];
    }

    map[key].push(
      typeof row.data.token === 'string'
        ? row.data.token
        : String(row.data.token),
    );
  }

  return map;
};

export const streamTokens = async ({
  editor,
  identifierType = 'templateSectionId',
  identifierValues = [],
  shouldFilterByPromptId = false,
  onStart,
  onPostStream,
  resolve = false,
  mockHtmlTokensByPromptId,
}: StreamTipTapNodesParams) => {
  const usesPrefetchedTokenPlayback = mockHtmlTokensByPromptId != null;

  if (usesPrefetchedTokenPlayback) {
    onStart?.();
  }

  const streamToNode = async ({
    identifierType,
    identifierValue,
  }: StreamToNodeParams = {}) => {
    const nodePos = getNodePosition(
      editor,
      identifierType as 'templateSectionId' | 'documentSectionId',
      identifierValue?.toString() ?? '-1',
    );

    if (!nodePos) {
      return;
    }

    const insertPos = getStreamContentInsertPosition(editor, nodePos, resolve);

    if (insertPos === null) {
      return Promise.resolve();
    }

    return new Promise((resolveSection) => {
      editor.commands?.streamContent(insertPos, async ({ write }) => {
        const promptKey = identifierValue?.toString() ?? '-1';

        if (mockHtmlTokensByPromptId) {
          const tokens = mockHtmlTokensByPromptId[promptKey] ?? [];

          if (tokens.length === 0) {
            resolveSection(true);

            return;
          }

          const chunkDelayMs = 100;

          // Keep the streamContent session open until every delayed write runs.
          // If the callback promise resolves before setTimeouts fire, the stream
          // can close and later write() calls are dropped.
          await new Promise<void>((resolvePlayback) => {
            let pending = tokens.length;

            const onChunkDone = () => {
              pending -= 1;

              if (pending === 0) {
                resolvePlayback();
              }
            };

            tokens.forEach((tokenChunk, i) => {
              setTimeout(
                () => {
                  try {
                    const contentToWrite =
                      typeof tokenChunk === 'string'
                        ? tokenChunk
                        : String(tokenChunk);

                    write({ partial: contentToWrite });
                    const nodePosition = getNodePosition(
                      editor,
                      identifierType as
                        | 'templateSectionId'
                        | 'documentSectionId',
                      promptKey,
                    );

                    if (nodePosition?.node.lastChild) {
                      scrollToLastNode(nodePosition.node.lastChild);
                    }
                  } finally {
                    onChunkDone();
                  }
                },
                chunkDelayMs * (i + 1),
              );
            });
          });

          resolveSection(true);

          return;
        }

        resolveSection(true);
      });
    });
  };

  if (identifierValues.length === 0) {
    await streamToNode({
      identifierType,
      identifierValue: '-1',
    });
  } else {
    for (const sectionId of identifierValues) {
      await streamToNode({
        identifierType,
        identifierValue: sectionId,
        shouldFilterByPromptId,
      });
    }
  }

  await onPostStream?.();
};
